/**
 * Favorite Views CRUD against `/core/favorite-views`.
 *
 * Every mutation calls `refetchRoutes()` as well as invalidating the local
 * list: a view IS a sidebar route, and the sidebar is built server-side from
 * `/platform/tabs`. Skipping that leaves the new view invisible until a hard
 * refresh — the single most likely way this feature "doesn't work".
 *
 * 404 = server not deployed yet; surfaced as `notDeployed` so the Save button
 * can go quiet instead of throwing a toast on every click.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import axiosInstance from "@/lib/axios";
import { favoriteViewsEndpoint } from "@/config/endpoints";
import { refetchRoutes } from "@/hooks/api/useRouterData";
import type { FavoriteView, FavoriteViewPayload } from "../types";

const KEY = ["favorite-views"];

export function isEndpointMissing(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export interface CreateFavoriteViewInput {
  name: string;
  iconName?: string;
  filters: FavoriteViewPayload;
  isShared?: boolean;
}

export interface UpdateFavoriteViewInput {
  id: number;
  name?: string;
  iconName?: string;
  filters?: FavoriteViewPayload;
  isShared?: boolean;
  isActive?: boolean;
}

export function useFavoriteViews(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<FavoriteView[]> => {
      const res = await axiosInstance.get(favoriteViewsEndpoint);
      return (res.data?.data ?? []) as FavoriteView[];
    },
    staleTime: 60_000,
    // `useViewPinnedStageId` runs on EVERY route (it sits under
    // `useDynamicFilterParams`), but only a view route has a payload to read —
    // off a view it opts out so no page pays for a request it can't use.
    // Entries already in the cache are still returned.
    enabled: options?.enabled ?? true,
  });

  // A view is a sidebar route as much as a record — both caches must move
  // together or the sidebar and the list disagree.
  const settle = async () => {
    await queryClient.invalidateQueries({ queryKey: KEY });
    await refetchRoutes();
  };

  const createView = useMutation({
    mutationFn: async (input: CreateFavoriteViewInput): Promise<FavoriteView> => {
      const res = await axiosInstance.post(favoriteViewsEndpoint, input);
      return res.data?.data as FavoriteView;
    },
    onSuccess: settle,
  });

  const updateView = useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: UpdateFavoriteViewInput): Promise<FavoriteView> => {
      const res = await axiosInstance.patch(
        `${favoriteViewsEndpoint}/${id}`,
        patch,
      );
      return res.data?.data as FavoriteView;
    },
    onSuccess: settle,
  });

  const deleteView = useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axiosInstance.delete(`${favoriteViewsEndpoint}/${id}`);
    },
    onSuccess: settle,
  });

  /** Full ordered id list — index becomes `order`. Own views only server-side. */
  const reorderViews = useMutation({
    mutationFn: async (ids: number[]): Promise<void> => {
      await axiosInstance.patch(`${favoriteViewsEndpoint}/reorder`, { ids });
    },
    onSuccess: settle,
  });

  /**
   * Rewrites the `view-<id>` route key across every role. Also invalidates the
   * role caches, since the role editor reads those keys back.
   */
  const setAudience = useMutation({
    mutationFn: async ({
      id,
      roleIds,
    }: {
      id: number;
      roleIds: number[];
    }): Promise<void> => {
      await axiosInstance.patch(`${favoriteViewsEndpoint}/${id}/audience`, {
        roleIds,
      });
    },
    onSuccess: async () => {
      await settle();
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  const notDeployed = isEndpointMissing(query.error);

  return {
    views: query.data ?? [],
    isLoading: query.isLoading,
    notDeployed,
    error: notDeployed ? null : query.error,
    createView,
    updateView,
    deleteView,
    reorderViews,
    setAudience,
  };
}

/** The roles that may see a shared view (`view-<id>` holders). */
export function useFavoriteViewAudience(id: number | null) {
  return useQuery({
    queryKey: ["favorite-views", "audience", id],
    queryFn: async (): Promise<number[]> => {
      const res = await axiosInstance.get(
        `${favoriteViewsEndpoint}/${id}/audience`,
      );
      return (res.data?.data?.roleIds ?? []) as number[];
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
